import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users, MapPin, Globe2, Radio, Info, Loader2, Save, RotateCcw,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 1E.7 — Collector Privacy & Matching Foundation.
 *
 * Owner-only tab. Reads + writes the collector's matching preferences.
 * NO trading / matching / messaging / discovery UI — this is settings
 * plumbing only, so future phases can flip a single flag to opt in.
 */
const DEFAULTS = {
  matching_enabled: false,
  share_missing_list: false,
  share_duplicate_list: false,
  public_location_enabled: false,
  search_radius_km: 50,
  preferred_languages: [],
  location: { country: null, state_province: null, city: null, coordinates: null },
};

const LANGUAGE_CHOICES = [
  { code: "en",    label: "English" },
  { code: "es",    label: "Spanish" },
  { code: "pt",    label: "Portuguese" },
  { code: "fr",    label: "French" },
  { code: "it",    label: "Italian" },
  { code: "de",    label: "German" },
  { code: "ar",    label: "Arabic" },
  { code: "ja",    label: "Japanese" },
  { code: "ko",    label: "Korean" },
  { code: "zh",    label: "Chinese" },
];

export default function MatchingTab() {
  const [state, setState] = useState({ loading: true, initial: DEFAULTS, current: DEFAULTS });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/me/match-preferences")
      .then((r) => {
        if (!alive) return;
        const p = r.data.preferences || DEFAULTS;
        setState({ loading: false, initial: p, current: p });
      })
      .catch(() => { if (alive) setState({ loading: false, initial: DEFAULTS, current: DEFAULTS }); });
    return () => { alive = false; };
  }, []);

  const p = state.current;
  const dirty = JSON.stringify(state.initial) !== JSON.stringify(state.current);

  const update = (patch) => setState((s) => ({ ...s, current: { ...s.current, ...patch } }));
  const updateLocation = (patch) => setState((s) => ({
    ...s, current: { ...s.current, location: { ...s.current.location, ...patch } },
  }));
  const toggleLanguage = (code) => setState((s) => {
    const has = s.current.preferred_languages.includes(code);
    const next = has
      ? s.current.preferred_languages.filter((c) => c !== code)
      : [...s.current.preferred_languages, code];
    return { ...s, current: { ...s.current, preferred_languages: next } };
  });

  const reset = () => setState((s) => ({ ...s, current: s.initial }));

  const save = async () => {
    setSaving(true);
    try {
      // Send the full current payload; the server handles partial patch semantics
      // fine even when everything is included.
      const body = {
        matching_enabled:        p.matching_enabled,
        share_missing_list:      p.share_missing_list,
        share_duplicate_list:    p.share_duplicate_list,
        public_location_enabled: p.public_location_enabled,
        search_radius_km:        Number(p.search_radius_km) || DEFAULTS.search_radius_km,
        preferred_languages:     p.preferred_languages,
        location: {
          country:        p.location?.country || "",
          state_province: p.location?.state_province || "",
          city:           p.location?.city || "",
          coordinates:    p.location?.coordinates || null,
        },
      };
      const r = await api.patch("/me/match-preferences", body);
      const next = r.data.preferences || p;
      setState({ loading: false, initial: next, current: next });
      toast.success("Matching preferences saved.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save preferences.";
      toast.error(typeof msg === "string" ? msg : "Could not save preferences.");
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="matching-loading" className="text-sm text-[var(--smoke)] py-10">
        <Loader2 className="inline animate-spin mr-2" size={14} /> Loading preferences…
      </div>
    );
  }

  return (
    <div data-testid="matching-tab" className="space-y-8 pb-24 relative">
      {/* Intro */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-5"
            data-testid="matching-intro">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-[var(--ink)] shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-lg text-[var(--ink)]">Matching foundation</div>
            <p className="text-sm text-[var(--ink)]/80 mt-1">
              Set your preferences here. When collector-to-collector matching arrives,
              these choices tell the platform whether to include you and how to find good matches.
              Your <strong>Public profile</strong>, <strong>Matching visibility</strong>,
              and <strong>Album visibility</strong> are independent — updating one never
              changes the others.
            </p>
          </div>
        </div>
      </div>

      {/* Matching */}
      <Section
        testid="section-matching"
        icon={Users}
        title="Matching"
        subtitle="Control whether other collectors can see you as a potential match."
      >
        <Toggle
          testid="toggle-matching-enabled"
          label="Include me in future match searches"
          hint="Turn this on when you're ready to be discovered. Off by default."
          checked={p.matching_enabled}
          onChange={(v) => update({ matching_enabled: v })}
        />
        <Toggle
          testid="toggle-share-missing"
          label="Share my missing sticker list for matching"
          hint="Only shared with matching flows. Not shown on your public profile."
          checked={p.share_missing_list}
          onChange={(v) => update({ share_missing_list: v })}
          disabled={!p.matching_enabled}
        />
        <Toggle
          testid="toggle-share-duplicate"
          label="Share my duplicate sticker list for matching"
          hint="Only shared with matching flows. Not shown on your public profile."
          checked={p.share_duplicate_list}
          onChange={(v) => update({ share_duplicate_list: v })}
          disabled={!p.matching_enabled}
        />

        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]"
                  htmlFor="match-radius">
            Preferred search radius
          </label>
          <div className="mt-1 flex items-center gap-3">
            <input
              id="match-radius"
              data-testid="input-search-radius"
              type="range"
              min={5}
              max={500}
              step={5}
              value={p.search_radius_km}
              onChange={(e) => update({ search_radius_km: Number(e.target.value) })}
              className="flex-1 accent-[var(--panini-red)]"
              disabled={!p.matching_enabled}
            />
            <span className="w-20 text-right font-mono text-sm text-[var(--ink)]"
                   data-testid="radius-display">
              {p.search_radius_km} km
            </span>
          </div>
        </div>
      </Section>

      {/* Location */}
      <Section
        testid="section-location"
        icon={MapPin}
        title="Location"
        subtitle="Coarse location helps future matching. Exact coordinates are never shown publicly."
      >
        <Toggle
          testid="toggle-public-location"
          label="Show my country / region on my public profile"
          hint="City and state stay private unless you also enable this."
          checked={p.public_location_enabled}
          onChange={(v) => update({ public_location_enabled: v })}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <TextField
            testid="input-country"
            label="Country (2-letter code)"
            value={p.location?.country || ""}
            onChange={(v) => updateLocation({ country: v ? v.toUpperCase() : "" })}
            placeholder="US, MX, BR…"
            maxLength={2}
          />
          <TextField
            testid="input-state"
            label="State / Province"
            value={p.location?.state_province || ""}
            onChange={(v) => updateLocation({ state_province: v })}
            placeholder="Florida, São Paulo…"
            maxLength={100}
          />
          <TextField
            testid="input-city"
            label="City"
            value={p.location?.city || ""}
            onChange={(v) => updateLocation({ city: v })}
            placeholder="Miami, São Paulo…"
            maxLength={100}
          />
        </div>
        <p className="text-xs text-[var(--smoke)] mt-3" data-testid="coords-notice">
          Exact address / ZIP code is never collected. Coordinates (when set)
          are used privately for future distance-based matching and are never
          displayed publicly.
        </p>
      </Section>

      {/* Languages */}
      <Section
        testid="section-languages"
        icon={Globe2}
        title="Languages"
        subtitle="Pick languages you're comfortable trading in. Optional."
      >
        <div className="flex flex-wrap gap-2" role="group" aria-label="Preferred languages">
          {LANGUAGE_CHOICES.map((lang) => {
            const active = p.preferred_languages.includes(lang.code);
            return (
              <button
                key={lang.code}
                type="button"
                data-testid={`lang-chip-${lang.code}`}
                onClick={() => toggleLanguage(lang.code)}
                aria-pressed={active}
                className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-widest transition ${
                  active
                    ? "bg-[var(--panini-red)] text-white border-[var(--panini-red)]"
                    : "bg-white text-[var(--ink)] border-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
                }`}
              >
                {lang.label}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Public profile note (info-only, independent setting) */}
      <Section
        testid="section-public-profile-note"
        icon={Radio}
        title="Public profile"
        subtitle="This is a separate switch. Manage it under Profile → Privacy."
      >
        <p className="text-sm text-[var(--smoke)]">
          Turning off matching does <strong>not</strong> hide your public profile.
          Turning off your public profile does <strong>not</strong> disable matching.
          These are independent so you can share one without the other.
        </p>
      </Section>

      {/* Sticky save bar */}
      {dirty && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 shadow-[4px_4px_0_var(--ink)]"
              data-testid="matching-save-bar">
          <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">
            Unsaved changes
          </span>
          <button
            data-testid="matching-reset"
            type="button"
            onClick={reset}
            disabled={saving}
            className="btn-ghost text-xs"
          >
            <RotateCcw size={13} /> Reset
          </button>
          <button
            data-testid="matching-save"
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-red text-xs"
          >
            {saving
              ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
              : <><Save size={13} /> Save</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
function Section({ testid, icon: Icon, title, subtitle, children }) {
  return (
    <section data-testid={testid}
              className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--ink)]">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center shrink-0">
          <Icon size={18} className="text-[var(--ink)]" />
        </div>
        <div>
          <h3 className="font-display text-xl text-[var(--ink)]">{title}</h3>
          {subtitle && <p className="text-sm text-[var(--smoke)] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Toggle({ testid, label, hint, checked, onChange, disabled }) {
  return (
    <div className={`flex items-start justify-between gap-3 rounded-xl border-2 border-[var(--ink)]/20 bg-[var(--cream)] px-4 py-3 ${disabled ? "opacity-50" : ""}`}
          data-testid={`${testid}-row`}>
      <div className="min-w-0">
        <div className="text-sm font-bold text-[var(--ink)]">{label}</div>
        {hint && <div className="text-xs text-[var(--smoke)] mt-0.5">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`shrink-0 relative w-11 h-6 rounded-full border-2 border-[var(--ink)] transition ${
          checked ? "bg-[var(--panini-red)]" : "bg-white"
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white border-2 border-[var(--ink)] transition-all ${
            checked ? "left-[22px]" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function TextField({ testid, label, value, onChange, placeholder, maxLength }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">
        {label}
      </span>
      <input
        data-testid={testid}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[var(--ink)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
      />
    </label>
  );
}
